import { Plane, SeparatorHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export default function Toolbar({ editor }) {
  if (!editor) {
    return null
  }

  const handleInsertFlightItinerary = () => {
    editor.chain().focus().setFlightItinerary({
      flights: [
        {
          origin: 'FRA',
          destination: 'JFK',
          flightNumber: 'LH400',
          departure: '14:30',
          arrival: '17:45'
        },
        {
          origin: 'JFK',
          destination: 'LAX',
          flightNumber: 'LH9999',
          departure: '20:15',
          arrival: '23:30'
        }
      ],
      passengerName: null
    }).run()
  }

  const ToolbarButton = ({ active, onClick, disabled, children, className = '' }) => (
    <Button
      size="sm"
      variant={active ? 'default' : 'ghost'}
      onClick={onClick}
      disabled={disabled}
      className={`h-8 px-3 text-sm ${active ? 'bg-sidebar-active text-action-text' : 'text-slate-700'} ${className}`}
    >
      {children}
    </Button>
  )

  return (
    <div className="flex items-center gap-1 flex-wrap p-2 border-b border-slate-200">
      {/* Text Formatting */}
      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className="font-bold"
      >
        Bold
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className="italic"
      >
        Italic
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        className="line-through"
      >
        Strike
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editor.can().chain().focus().toggleCode().run()}
        className="font-mono"
      >
        Code
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Headings */}
      <ToolbarButton
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        H1
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        H2
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        H3
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Lists */}
      <ToolbarButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        • List
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        1. List
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Blocks */}
      <ToolbarButton
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        Quote
      </ToolbarButton>

      <ToolbarButton
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className="font-mono"
      >
        Code Block
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        ─ HR
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Custom Nodes */}
      <ToolbarButton
        active={editor.isActive('flightItinerary')}
        onClick={handleInsertFlightItinerary}
        className="gap-1"
      >
        <Plane className="h-4 w-4" />
        Flight
      </ToolbarButton>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant={editor.isActive('pageBreak') ? 'default' : 'ghost'}
              onClick={() => editor.chain().focus().setPageBreak().run()}
              className={`h-8 px-3 text-sm gap-1 ${editor.isActive('pageBreak') ? 'bg-sidebar-active text-action-text' : 'text-slate-700'}`}
            >
              <SeparatorHorizontal className="h-4 w-4" />
              Page Break
            </Button>
          </TooltipTrigger>
          <TooltipContent>Insert Page Break (Ctrl+Enter)</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* History */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
      >
        ↶ Undo
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
      >
        ↷ Redo
      </ToolbarButton>
    </div>
  )
}
